import { MutationCache, QueryCache, QueryClient, type QueryKey } from "@tanstack/react-query"
import { ApiError } from "@/lib/api-client"
import { AI_HEALTH_KEY, pointsToAiOrConnectionProblem } from "@/lib/ai-health"

/** At most one immediate AI-health re-check per this many ms, however many requests fail at once. */
const HEALTH_NUDGE_INTERVAL_MS = 3_000

export function createQueryClient() {
  let lastNudge = 0

  // A request that fails with a credits / key / rate-limit / connection error re-checks AI health at once,
  // so the global warning appears right away instead of at the next poll.
  const nudgeHealth = (error: unknown, queryKey?: QueryKey) => {
    if (queryKey?.[0] === AI_HEALTH_KEY[0] || !pointsToAiOrConnectionProblem(error)) return
    const now = Date.now()
    if (now - lastNudge < HEALTH_NUDGE_INTERVAL_MS) return
    lastNudge = now
    void client.invalidateQueries({ queryKey: AI_HEALTH_KEY })
  }

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => nudgeHealth(error, query.queryKey) }),
    mutationCache: new MutationCache({ onError: (error) => nudgeHealth(error) }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        // Client errors (bad id, auth) won't fix themselves; only retry network/server failures.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
      },
      mutations: {
        retry: false,
      },
    },
  })
  return client
}
